import type {
  AddressSuggestion,
  Agent,
  AgendaEntry,
  AgentListing,
  PropertyListing,
  Bid,
  Billing,
  Client,
  ClientFinancials,
  CustomDealTemplate,
  Comment,
  Deal,
  DealDetail,
  DealDocument,
  DealSummary,
  DocumentStatus,
  MemberInvite,
  Membership,
  MortgageProfile,
  Organization,
  Position,
  ProMe,
  Property,
  ResolvedAddress,
  SigningRequest,
  Step,
  StepEdge,
  StepTypeKey,
  Viewing,
} from "@biedradar/shared/pro";
import type { CreateDealInput, ProApi, StepPatch } from "./api";

// Empty origin → relative `/api/pro/...`, which the Vite dev proxy forwards to
// the API on :3000. In production set VITE_API_ORIGIN to the API host.
const ORIGIN = import.meta.env.VITE_API_ORIGIN ?? "";
const BASE = `${ORIGIN}/api/pro`;

async function req<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    ...init,
  });
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  const body: unknown = text ? JSON.parse(text) : null;
  if (!res.ok) {
    const msg =
      body && typeof body === "object" && "error" in body
        ? String((body as { error: unknown }).error)
        : `HTTP ${res.status}`;
    throw new Error(msg);
  }
  return body as T;
}

export const httpApi: ProApi = {
  getMe: () => req<ProMe>("/me"),
  createOrg: (input) =>
    req<ProMe>("/onboarding", {
      method: "POST",
      body: JSON.stringify(input ?? {}),
    }),
  getOrg: () => req<Organization>("/org"),
  getMembers: () => req<Membership[]>("/members"),
  listClients: () => req<Client[]>("/clients"),
  updateClient: (clientId, patch) =>
    req<Client>(`/clients/${clientId}`, { method: "PATCH", body: JSON.stringify(patch) }),
  listDeals: () => req<DealSummary[]>("/deals"),
  getDeal: async (id: string) => {
    const res = await fetch(`${BASE}/deals/${id}`, { credentials: "include" });
    if (res.status === 404) return null;
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return (await res.json()) as DealDetail;
  },
  createDeal: (input: CreateDealInput) =>
    req<DealDetail>("/deals", { method: "POST", body: JSON.stringify(input) }),
  updateDeal: (dealId, patch) =>
    req<Deal>(`/deals/${dealId}`, { method: "PATCH", body: JSON.stringify(patch) }),
  deleteDeal: (dealId) => req<void>(`/deals/${dealId}`, { method: "DELETE" }),
  createStep: (dealId: string, typeKey: StepTypeKey, position: Position) =>
    req<Step>(`/deals/${dealId}/steps`, {
      method: "POST",
      body: JSON.stringify({ typeKey, position }),
    }),
  updateStep: (stepId: string, patch: StepPatch) =>
    req<Step>(`/steps/${stepId}`, { method: "PATCH", body: JSON.stringify(patch) }),
  deleteStep: (stepId: string) => req<void>(`/steps/${stepId}`, { method: "DELETE" }),
  createEdge: (dealId: string, source: string, target: string) =>
    req<StepEdge | null>(`/deals/${dealId}/edges`, {
      method: "POST",
      body: JSON.stringify({ sourceStepId: source, targetStepId: target }),
    }),
  deleteEdge: (edgeId: string) => req<void>(`/edges/${edgeId}`, { method: "DELETE" }),

  updateFinancials: (clientId, input) =>
    req<ClientFinancials>(`/clients/${clientId}/financials`, {
      method: "PATCH",
      body: JSON.stringify(input),
    }),
  updateMortgage: (dealId, input) =>
    req<MortgageProfile>(`/deals/${dealId}/mortgage`, {
      method: "PATCH",
      body: JSON.stringify(input),
    }),
  updateProperty: (dealId, patch) =>
    req<Property>(`/deals/${dealId}/property`, { method: "PATCH", body: JSON.stringify(patch) }),
  upsertBid: (dealId, input) =>
    req<Bid>(`/deals/${dealId}/bid`, { method: "PUT", body: JSON.stringify(input) }),
  addViewing: (dealId, input) =>
    req<Viewing>(`/deals/${dealId}/viewings`, { method: "POST", body: JSON.stringify(input) }),
  updateViewing: (viewingId, patch) =>
    req<Viewing>(`/viewings/${viewingId}`, { method: "PATCH", body: JSON.stringify(patch) }),
  deleteViewing: (viewingId) => req<void>(`/viewings/${viewingId}`, { method: "DELETE" }),
  listAgenda: () => req<AgendaEntry[]>("/agenda"),
  listOrgAgents: () => req<AgentListing[]>("/agents"),
  listOrgProperties: () => req<PropertyListing[]>("/properties"),
  addAgent: (dealId, input) =>
    req<Agent>(`/deals/${dealId}/agents`, { method: "POST", body: JSON.stringify(input) }),
  updateAgent: (agentId, patch) =>
    req<Agent>(`/agents/${agentId}`, { method: "PATCH", body: JSON.stringify(patch) }),
  deleteAgent: (agentId) => req<void>(`/agents/${agentId}`, { method: "DELETE" }),

  listTemplates: () => req<CustomDealTemplate[]>("/templates"),
  createTemplate: (input) =>
    req<CustomDealTemplate>("/templates", { method: "POST", body: JSON.stringify(input) }),
  deleteTemplate: (id) => req<void>(`/templates/${id}`, { method: "DELETE" }),

  updateOrg: (patch) => req<Organization>("/org", { method: "PATCH", body: JSON.stringify(patch) }),
  dismissViewHint: () => req<Organization>("/view-hint/dismiss", { method: "POST" }),
  updateMemberRole: (id, role) =>
    req<Membership>(`/members/${id}`, { method: "PATCH", body: JSON.stringify({ role }) }),
  removeMember: (id) => req<void>(`/members/${id}`, { method: "DELETE" }),
  inviteMember: (email, role) =>
    req<{ invite: MemberInvite; token: string }>("/members/invite", { method: "POST", body: JSON.stringify({ email, role }) }),
  listMemberInvites: () => req<MemberInvite[]>("/members/invites"),
  revokeMemberInvite: (id) => req<void>(`/members/invites/${id}`, { method: "DELETE" }),
  acceptInvite: (token) =>
    req<{ organizationId: string }>("/accept-invite", { method: "POST", body: JSON.stringify({ token }) }),

  geoSuggest: (q) => req<AddressSuggestion[]>(`/geo/suggest?q=${encodeURIComponent(q)}`),
  geoResolve: async (id) => {
    const res = await fetch(`${BASE}/geo/resolve?id=${encodeURIComponent(id)}`, { credentials: "include" });
    if (!res.ok) return null;
    return (await res.json()) as ResolvedAddress;
  },

  requestDocument: (dealId, input) =>
    req<DealDocument>(`/deals/${dealId}/documents/request`, {
      method: "POST",
      body: JSON.stringify(input),
    }),
  uploadDocument: (dealId, input) =>
    req<DealDocument>(`/deals/${dealId}/documents/upload`, {
      method: "POST",
      body: JSON.stringify(input),
    }),
  setDocumentStatus: (docId: string, status: DocumentStatus) =>
    req<DealDocument>(`/documents/${docId}/status`, {
      method: "PATCH",
      body: JSON.stringify({ status }),
    }),
  documentFileUrl: (docId: string) => `${BASE}/documents/${docId}/file`,
  addComment: (dealId, input) =>
    req<Comment>(`/deals/${dealId}/comments`, {
      method: "POST",
      body: JSON.stringify(input),
    }),
  createSigningRequest: (dealId, input) =>
    req<{ request: SigningRequest; token: string }>(
      `/deals/${dealId}/signing-requests`,
      { method: "POST", body: JSON.stringify(input) },
    ),
  createPortalInvite: (dealId: string, email: string) =>
    req<{ token: string }>(`/deals/${dealId}/portal-invite`, {
      method: "POST",
      body: JSON.stringify({ email }),
    }),
  listAllDocuments: () => req<DealDocument[]>("/documents"),

  getBilling: () => req<Billing>("/billing"),
  startTrial: (plan) =>
    req<Billing>("/billing/trial", { method: "POST", body: JSON.stringify({ plan }) }),
  subscribeBilling: async (plan) => {
    try {
      return await req<{ checkoutUrl: string | null }>("/billing/subscribe", {
        method: "POST",
        body: JSON.stringify({ plan }),
      });
    } catch (err) {
      // Mollie unconfigured → 503 billing_unavailable. Degrade to "no checkout
      // URL" so the UI can show a clear message instead of erroring out.
      if (err instanceof Error && err.message === "billing_unavailable") {
        return { checkoutUrl: null };
      }
      throw err;
    }
  },
};
